import React from "react";
import { format, parseISO } from "date-fns";
import AutoSizer from "react-virtualized-auto-sizer";
import { VariableSizeList as List } from "react-window";
import { Box, Chip, ListItem, Typography } from "@mui/material";
import { RiCalendarLine, RiCheckLine } from "@remixicon/react";

import { AutoSizerPropsT } from "../../../types/ScheduleForm";
import { SchedulingEvent_T } from "../../../types/Scheduling";

const LIST_ITEM_HEIGHT = 56;

type RecurringScheduledEventsProps_T = {
  isFormValid: boolean;
  generatedSchedule: SchedulingEvent_T[];
};

export default function RecurringScheduledEvents({
  isFormValid,
  generatedSchedule,
}: RecurringScheduledEventsProps_T) {
  return (
    <Box
      mt={0}
      height={
        generatedSchedule.length > 0 && generatedSchedule.length < 5
          ? LIST_ITEM_HEIGHT * generatedSchedule.length
          : 240 // purely cosmetic
      }
      maxHeight={240}
      borderRadius="4px"
      overflow="auto"
      sx={{
        border: "1px solid #bdbdbd",
        padding: generatedSchedule.length === 0 ? "16px" : 0,
      }}
    >
      {!isFormValid && (
        <Typography variant="body2" color="text.secondary">
          Please fill out all required fields to generate the schedule.
        </Typography>
      )}
      {isFormValid && generatedSchedule.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No scheduled deliveries for selected dates and frequency.
        </Typography>
      )}
      {isFormValid && generatedSchedule.length > 0 && (
        <AutoSizer>
          {({ height, width }: AutoSizerPropsT) => (
            <List
              height={height}
              itemCount={generatedSchedule.length}
              itemSize={() => LIST_ITEM_HEIGHT}
              width={width}
              overscanCount={10}
            >
              {({ index, style }) => {
                const item: SchedulingEvent_T = generatedSchedule[index];
                return (
                  <ListItem
                    sx={{
                      ...style,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      padding: "4px 16px",
                      height: LIST_ITEM_HEIGHT,
                      borderBottom: "1px solid #bdbdbd",
                    }}
                  >
                    <Box
                      display="flex"
                      flexDirection="row"
                      alignItems="center"
                      gap={1}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        width={24}
                        height={24}
                      >
                        <RiCalendarLine size={16} color="primary.main" />
                      </Box>
                      <Typography variant="body2">
                        {format(
                          parseISO(item.target_fulfillment_date || ""),
                          "MMM d, yyyy"
                        )}{" "}
                        ({item.target_fulfillment_dow})
                      </Typography>
                    </Box>
                    {item.schedule_volume_upgrade && (
                      <Box
                        display="flex"
                        alignItems="center"
                        flexDirection="row"
                        gap={1}
                        sx={{ marginLeft: "auto" }}
                      >
                        <Chip
                          icon={<RiCheckLine size={16} />}
                          label="Volume Upgrade"
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ px: 1 }}
                        />
                      </Box>
                    )}
                  </ListItem>
                );
              }}
            </List>
          )}
        </AutoSizer>
      )}
    </Box>
  );
}
